import { isInvoiceAmountFilterValid } from "@/domain/invoices/invoiceFilterState";
import type { CreditNoteRow } from "@/types/api";

function dateToUnixStart(iso: string): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(`${iso}T00:00:00.000Z`);
  return Number.isNaN(t) ? undefined : Math.floor(t / 1000);
}

function dateToUnixEnd(iso: string): number | undefined {
  if (!iso) return undefined;
  const t = Date.parse(`${iso}T23:59:59.999Z`);
  return Number.isNaN(t) ? undefined : Math.floor(t / 1000);
}

function parseMajor(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export interface CreditNoteSearchParams {
  customerIds?: string[];
  statuses?: string[];
  dateFrom?: string;
  dateTo?: string;
  amountMin?: string;
  amountMax?: string;
  tableSearch?: string;
}

export function filterCreditNotes(
  rows: CreditNoteRow[],
  params: CreditNoteSearchParams,
): CreditNoteRow[] {
  const {
    customerIds = [],
    statuses = [],
    dateFrom = "",
    dateTo = "",
    amountMin = "",
    amountMax = "",
    tableSearch = "",
  } = params;

  let list = [...rows];

  if (customerIds.length) {
    const set = new Set(customerIds);
    list = list.filter((r) => set.has(r.customer_id));
  }
  if (statuses.length) {
    const set = new Set(statuses);
    list = list.filter((r) => set.has(r.status));
  }

  const from = dateToUnixStart(dateFrom);
  const to = dateToUnixEnd(dateTo);
  if (from !== undefined) list = list.filter((r) => r.created >= from);
  if (to !== undefined) list = list.filter((r) => r.created <= to);

  const min = isInvoiceAmountFilterValid(amountMin, amountMax)
    ? parseMajor(amountMin)
    : null;
  const max = isInvoiceAmountFilterValid(amountMin, amountMax)
    ? parseMajor(amountMax)
    : null;
  if (min !== null || max !== null) {
    list = list.filter((row) => {
      const major = row.amount / 100;
      if (min !== null && major < min) return false;
      if (max !== null && major > max) return false;
      return true;
    });
  }

  const q = tableSearch.trim().toLowerCase();
  if (q) {
    list = list.filter(
      (r) =>
        r.id.toLowerCase().includes(q) ||
        (r.number?.toLowerCase().includes(q) ?? false) ||
        (r.invoice_id?.toLowerCase().includes(q) ?? false) ||
        (r.invoice_number?.toLowerCase().includes(q) ?? false),
    );
  }

  list.sort((a, b) => b.created - a.created);
  return list;
}
